import React from 'react';
import { Card, Statistic, Tag, Space, Typography, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { ObservabilityMetric, SLOResult, DisplayHints } from './types';
import moment from 'moment';

const { Text } = Typography;

interface MetricCardProps {
  metricKey: string;
  metric: ObservabilityMetric;
  sloResult?: SLOResult;
}

/**
 * Get SLO status icon and color
 */
const getSLODisplay = (status: SLOResult['status']) => {
  switch (status) {
    case 'passing':
      return { icon: <CheckCircleOutlined />, color: 'success', label: 'Passing' };
    case 'warning':
      return { icon: <WarningOutlined />, color: 'warning', label: 'Warning' };
    case 'error':
      return { icon: <CloseCircleOutlined />, color: 'error', label: 'Error' };
    case 'info':
      return { icon: <InfoCircleOutlined />, color: 'processing', label: 'Info' };
    default:
      return { icon: <QuestionCircleOutlined />, color: 'default', label: 'Unknown' };
  }
};

/**
 * Extract a displayable value from metric data
 */
const extractDisplayValue = (data: any): string | number => {
  if (data === null || data === undefined) {
    return '-';
  }

  // Handle simple string/number values
  if (typeof data === 'string' || typeof data === 'number') {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return `${data.length} items`;
  }

  // Handle total_hits (common in aggregation responses)
  if (typeof data === 'object' && 'total_hits' in data) {
    return data.total_hits;
  }

  // Handle nested metadata (e.g., lb_status)
  if (typeof data === 'object' && 'metadata.name' in data) {
    return String(data['metadata.name']);
  }

  // Handle spec.state (common in XC responses)
  if (typeof data === 'object' && 'spec.state' in data) {
    return String(data['spec.state']);
  }

  // Handle objects with a 'value' field
  if (typeof data === 'object' && 'value' in data) {
    const val = data.value;
    if (typeof val === 'object') return JSON.stringify(val).substring(0, 50);
    return val;
  }

  // For objects with array values, try to count items in the first array
  if (typeof data === 'object') {
    const keys = Object.keys(data);
    for (const key of keys) {
      if (Array.isArray(data[key])) {
        return `${data[key].length} ${key.replace('spec.', '').replace('default_', '')}`;
      }
    }
    if (keys.length === 0) return '-';
    return `${keys.length} fields`;
  }

  return String(data);
};

/**
 * Render a stat_card widget
 */
const StatCard: React.FC<{ metric: ObservabilityMetric; sloResult?: SLOResult }> = ({
  metric,
  sloResult
}) => {
  const displayValue = extractDisplayValue(metric.data);
  const hints: Partial<DisplayHints> = metric.display_hints || {};
  const sloDisplay = sloResult ? getSLODisplay(sloResult.status) : null;

  return (
    <Card
      size="small"
      style={{
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
      }}
      bodyStyle={{ padding: '16px' }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {hints.title || metric.name}
          </Text>
          {sloDisplay && (
            <Tooltip title={sloResult?.description}>
              <Tag
                icon={sloDisplay.icon}
                color={sloDisplay.color}
                style={{ fontSize: '10px', margin: 0 }}
              >
                SLO
              </Tag>
            </Tooltip>
          )}
        </div>
        <Statistic
          value={displayValue}
          suffix={hints.unit && hints.unit !== 'count' ? hints.unit : undefined}
          valueStyle={{
            fontSize: '24px',
            fontWeight: 600,
            color: hints.color || '#1890ff'
          }}
        />
        <Text type="secondary" style={{ fontSize: '10px' }}>
          {moment(metric.collected_at).format('HH:mm:ss')}
        </Text>
      </Space>
    </Card>
  );
};

/**
 * Safely extract a string value from data for display
 */
const extractStatusText = (data: any): string => {
  if (data === null || data === undefined) return 'Unknown';
  if (typeof data === 'string') return data;
  if (typeof data === 'number') return String(data);
  if (typeof data === 'boolean') return data ? 'Yes' : 'No';
  if (Array.isArray(data)) return `${data.length} items`;
  if (typeof data === 'object') {
    // Try common status fields
    if (data['spec.state']) return String(data['spec.state']);
    if (data['spec.cert_state']) return String(data['spec.cert_state']);
    if (data['metadata.name']) return String(data['metadata.name']);
    if (data.state) return String(data.state);
    if (data.status) return String(data.status);
    if (data.name) return String(data.name);
    // Fallback to field count
    return `${Object.keys(data).length} fields`;
  }
  return 'Unknown';
};

/**
 * Render a status_badge widget
 */
const StatusBadge: React.FC<{ metric: ObservabilityMetric; sloResult?: SLOResult }> = ({
  metric,
  sloResult
}) => {
  const hints: Partial<DisplayHints> = metric.display_hints || {};
  const data = metric.data || {};

  // Try to extract a status or name from the data
  let statusText = extractStatusText(data);
  let statusColor = 'default';

  // Determine color based on status text
  const textLower = statusText.toLowerCase();
  if (textLower.includes('ready') || textLower.includes('valid') || textLower.includes('success')) {
    statusColor = 'success';
  } else if (textLower.includes('error') || textLower.includes('fail')) {
    statusColor = 'error';
  } else if (textLower.includes('warning') || textLower.includes('pending')) {
    statusColor = 'warning';
  } else {
    statusColor = 'processing';
  }

  return (
    <Card
      size="small"
      style={{
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
      }}
      bodyStyle={{ padding: '12px 16px' }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {hints.title || metric.name}
        </Text>
        <Tag
          color={statusColor}
          style={{ fontSize: '12px', fontWeight: 500 }}
        >
          {statusText}
        </Tag>
        <Text type="secondary" style={{ fontSize: '10px' }}>
          {moment(metric.collected_at).format('HH:mm:ss')}
        </Text>
      </Space>
    </Card>
  );
};

/**
 * MetricCard component - renders based on display_hints.widget
 */
export const MetricCard: React.FC<MetricCardProps> = ({ metricKey, metric, sloResult }) => {
  const widgetType = metric.display_hints?.widget || 'stat_card';

  // Handle failed metrics
  if (metric.status === 'failed') {
    return (
      <Card
        size="small"
        style={{
          borderRadius: '8px',
          border: '1px solid #ffccc7',
          background: '#fff2f0'
        }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {metric.display_hints?.title || metric.name}
          </Text>
          <Tag icon={<CloseCircleOutlined />} color="error">
            Failed
          </Tag>
          {metric.error && (
            <Text type="danger" style={{ fontSize: '11px' }}>
              {metric.error.substring(0, 100)}...
            </Text>
          )}
        </Space>
      </Card>
    );
  }

  switch (widgetType) {
    case 'status_badge':
      return <StatusBadge metric={metric} sloResult={sloResult} />;
    case 'stat_card':
    default:
      return <StatCard metric={metric} sloResult={sloResult} />;
  }
};

export default MetricCard;
